/**
 * Frontend types for a customer-facing email design. The kinds, their labels
 * and their grouping come from the SHARED registry (`@notifi/shared-types/
 * email-kinds`) — the same one the backend sends from — so a new kind appears
 * here, in the builder palette and in the send without a second list.
 */
import {
  EMAIL_KINDS,
  EMAIL_KIND_GROUPS,
  EMAIL_TEMPLATE_KINDS,
  type EmailKindGroup,
  type EmailTemplateKind,
} from '@notifi/shared-types/email-kinds';
import type { EmailBrandingOverride, EmailLayout } from '@/types/emailLayout';

export { EMAIL_KINDS, EMAIL_KIND_GROUPS, EMAIL_TEMPLATE_KINDS };
export type { EmailKindGroup, EmailTemplateKind };

export type EmailTemplateStatus = 'DRAFT' | 'PUBLISHED';

const byKind = <T>(pick: (kind: EmailTemplateKind) => T): Record<EmailTemplateKind, T> =>
  Object.fromEntries(EMAIL_TEMPLATE_KINDS.map((k) => [k, pick(k)])) as Record<EmailTemplateKind, T>;

/** Human label for a kind (list + editor chrome). */
export const EMAIL_KIND_LABEL: Record<EmailTemplateKind, string> = byKind(
  (k) => EMAIL_KINDS[k].label,
);

/** Singular label for a single surface/tab (Business Unit editor). */
export const EMAIL_KIND_SURFACE_LABEL: Record<EmailTemplateKind, string> = byKind(
  (k) => EMAIL_KINDS[k].surface,
);

/** What a kind's email accompanies, singular and lowercase ("Use for {noun} sends"). */
export const EMAIL_KIND_NOUN: Record<EmailTemplateKind, string> = byKind(
  (k) => EMAIL_KINDS[k].noun,
);

/** Kinds grouped for a selector, in registry group order. */
export const EMAIL_KINDS_BY_GROUP: { group: EmailKindGroup; kinds: EmailTemplateKind[] }[] =
  EMAIL_KIND_GROUPS.map((group) => ({
    group,
    kinds: EMAIL_TEMPLATE_KINDS.filter((k) => EMAIL_KINDS[k].group === group),
  })).filter((g) => g.kinds.length > 0);

/** Catalog row — never the heavy HTML body. */
export interface EmailTemplateSummary {
  id: string;
  name: string;
  kind: EmailTemplateKind;
  status: EmailTemplateStatus;
  version: number;
  /** The tenant default for this kind (used when a BU has no assignment). */
  isDefault: boolean;
  hasUnpublishedChanges: boolean;
}

export interface EmailTemplateFull extends EmailTemplateSummary {
  subject: string;
  bodyHtml: string;
  /** The structured block layout the builder edits (null for legacy emails). */
  layout: EmailLayout | null;
  /**
   * The design's own brand override (colours / fonts); blank fields inherit the
   * tenant / Business-Unit brand at render. Null when it never set one.
   */
  branding: EmailBrandingOverride | null;
  /**
   * The PUBLISHED snapshot the send actually uses (null until first publish). The
   * working `subject`/`bodyHtml`/`layout` above may hold unpublished edits — a
   * "what will be sent" preview must read these.
   */
  publishedSubject: string | null;
  publishedBodyHtml: string | null;
  publishedLayout: EmailLayout | null;
  /** The brand override as PUBLISHED — what the send uses; a "what will be sent" preview reads this. */
  publishedBranding: EmailBrandingOverride | null;
}

export interface CreateEmailTemplateDto {
  kind: EmailTemplateKind;
  name: string;
  subject?: string;
  bodyHtml?: string;
  layout?: EmailLayout | null;
  branding?: EmailBrandingOverride | null;
}

export interface UpdateEmailTemplateDto {
  name?: string;
  subject?: string;
  bodyHtml?: string;
  layout?: EmailLayout | null;
  branding?: EmailBrandingOverride | null;
}

/** A platform starter a tenant can clone (catalog fields only; the layout is server-side). */
export interface EmailTemplateStarterSummary {
  slug: string;
  kind: EmailTemplateKind;
  name: string;
  description: string;
  subject: string;
}
